import { useEffect, useMemo, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { CheckCircle2, Loader2, QrCode, Wallet } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { goToLogin } from '../utils/authRedirect'
import api, { paymentApi } from '../utils/api'

const CASHBACK_ROUTE_OPTIONS = [
  { value: 'unactivated', label: '未激活路线 page3' },
  { value: 'activated_low_cost_15d', label: '15天低消耗路线 page3' }
]

function formatJson(value) {
  try {
    return JSON.stringify(value, null, 2)
  } catch (error) {
    return String(value || '')
  }
}

function isPaymentPaid(payload) {
  return Boolean(
    payload?.status === 1
    || payload?.isPaid
    || payload?.data?.status === 1
    || payload?.data?.isPaid
  )
}

export default function TestReminderFlow() {
  const { user, loading: authLoading } = useAuth()

  const [page4StageNo, setPage4StageNo] = useState('1')
  const [page4PlanId, setPage4PlanId] = useState('11')
  const [page4Loading, setPage4Loading] = useState(false)
  const [page4Error, setPage4Error] = useState('')
  const [page4Status, setPage4Status] = useState(null)
  const [page4OrderNo, setPage4OrderNo] = useState('')
  const [page4QrCodeUrl, setPage4QrCodeUrl] = useState('')
  const [page4OrderState, setPage4OrderState] = useState('idle')
  const [page4MrSync, setPage4MrSync] = useState(1)
  const [page4SyncTip, setPage4SyncTip] = useState('')
  const [page4Raw, setPage4Raw] = useState('')

  const [cashbackRouteKey, setCashbackRouteKey] = useState('unactivated')
  const [cashbackStageNo, setCashbackStageNo] = useState('3')
  const [cashbackRewardRecordId, setCashbackRewardRecordId] = useState('')
  const [cashbackLoading, setCashbackLoading] = useState(false)
  const [cashbackError, setCashbackError] = useState('')
  const [cashbackStatus, setCashbackStatus] = useState(null)
  const [cashbackQrCodeUrl, setCashbackQrCodeUrl] = useState('')
  const [cashbackBindToken, setCashbackBindToken] = useState('')
  const [cashbackPollingState, setCashbackPollingState] = useState('idle')
  const [cashbackPollRaw, setCashbackPollRaw] = useState('')

  const page4Offer = page4Status?.rechargeOffer || null
  const effectivePage4PlanId = Number(page4Offer?.planId || page4PlanId || 0)
  const effectiveCashbackRewardRecordId = Number(cashbackStatus?.rewardRecordId || cashbackRewardRecordId || 0)
  const cashbackReadyForQrcode = useMemo(() => {
    if (effectiveCashbackRewardRecordId > 0) {
      return true
    }

    return Boolean(
      cashbackStatus?.rewardRecordId
      && cashbackStatus?.rewardType === 'cashback_withdraw'
      && !cashbackStatus?.invalidated
    )
  }, [cashbackStatus, effectiveCashbackRewardRecordId])

  useEffect(() => {
    if (!authLoading && !user) {
      goToLogin('/test-reminder-flow')
    }
  }, [authLoading, user])

  useEffect(() => {
    if (page4OrderState !== 'pending' || !page4OrderNo) return undefined

    const timer = window.setInterval(async () => {
      try {
        const res = await paymentApi.checkStatus(page4OrderNo)
        if (isPaymentPaid(res?.data)) {
          const payload = res?.data
          setPage4MrSync(payload?.data?.isMrSync === 0 ? 0 : 1)
          setPage4SyncTip(payload?.message || '充值成功，余额正在同步，预计几分钟内到账')
          setPage4OrderState('success')
        }
      } catch (error) {
        console.error('查询 page4 支付状态失败:', error)
      }
    }, 3000)

    return () => window.clearInterval(timer)
  }, [page4OrderNo, page4OrderState])

  useEffect(() => {
    if (!cashbackBindToken || cashbackPollingState !== 'pending') return undefined

    const timer = window.setInterval(async () => {
      try {
        const res = await api.post('/v1/client/reminder/cashback/wechat-qrcode/status', {
          bindToken: cashbackBindToken
        })
        setCashbackPollRaw(formatJson(res.data))
        if (res.data?.authorized) {
          setCashbackPollingState('authorized')
          setCashbackQrCodeUrl('')
          return
        }
        if (res.data?.status === 'expired' || res.data?.expired) {
          setCashbackPollingState('expired')
        }
      } catch (error) {
        console.error('轮询微信提现授权状态失败:', error)
      }
    }, 2000)

    return () => window.clearInterval(timer)
  }, [cashbackBindToken, cashbackPollingState])

  const handleLoadPage4Status = async () => {
    setPage4Loading(true)
    setPage4Error('')
    setPage4Raw('')
    setPage4Status(null)
    setPage4QrCodeUrl('')
    setPage4OrderNo('')
    setPage4OrderState('idle')

    try {
      const res = await api.post('/v1/client/reminder/reward/status', {
        routeKey: 'low_balance_unpaid',
        stageNo: Number(page4StageNo || 1)
      })
      setPage4Status(res.data)
      setPage4Raw(formatJson(res.data))
      if (!res.data?.rechargeOffer) {
        setPage4Error('当前账号/阶段没有拿到 page4 的充值活动信息')
      }
    } catch (error) {
      setPage4Error(error.response?.data?.error || error.message || '加载 page4 状态失败')
    } finally {
      setPage4Loading(false)
    }
  }

  const handleCreatePage4Order = async () => {
    if (!effectivePage4PlanId) {
      setPage4Error('请先填写有效的 planId')
      return
    }

    setPage4Loading(true)
    setPage4Error('')
    setPage4QrCodeUrl('')
    setPage4OrderNo('')
    setPage4OrderState('idle')

    try {
      const res = await paymentApi.createOrder({
        payType: 'wechat',
        planId: effectivePage4PlanId,
        planType: 'recharge',
        reminderRouteKey: 'low_balance_unpaid',
        reminderPageKey: 'page4',
        reminderStageNo: Number(page4StageNo || 1)
      })

      setPage4Raw(formatJson(res.data))
      setPage4QrCodeUrl(res.data?.qrCodeUrl || '')
      setPage4OrderNo(res.data?.orderNo || '')
      setPage4OrderState(res.data?.qrCodeUrl ? 'pending' : 'idle')
      if (!res.data?.qrCodeUrl) {
        setPage4Error('下单成功但未返回支付二维码')
      }
    } catch (error) {
      setPage4Error(error.response?.data?.error || error.message || '创建支付订单失败')
    } finally {
      setPage4Loading(false)
    }
  }

  const handleLoadCashbackStatus = async () => {
    setCashbackLoading(true)
    setCashbackError('')
    setCashbackStatus(null)
    setCashbackQrCodeUrl('')
    setCashbackBindToken('')
    setCashbackPollingState('idle')
    setCashbackPollRaw('')

    try {
      const res = await api.post('/v1/client/reminder/reward/status', {
        routeKey: cashbackRouteKey,
        stageNo: Number(cashbackStageNo || 3)
      })
      setCashbackStatus(res.data)
      setCashbackPollRaw(formatJson(res.data))
      if (res.data?.rewardType !== 'cashback_withdraw') {
        setCashbackError('当前账号在该路线/阶段下没有可测试的微信提现奖励记录')
      }
    } catch (error) {
      setCashbackError(error.response?.data?.error || error.message || '加载微信提现状态失败')
    } finally {
      setCashbackLoading(false)
    }
  }

  const handleCreateCashbackQrcode = async () => {
    if (!effectiveCashbackRewardRecordId) {
      setCashbackError('请先填写有效的 rewardRecordId')
      return
    }

    setCashbackLoading(true)
    setCashbackError('')
    setCashbackQrCodeUrl('')
    setCashbackBindToken('')
    setCashbackPollingState('idle')

    try {
      const res = await api.post('/v1/client/reminder/cashback/wechat-qrcode', {
        rewardRecordId: effectiveCashbackRewardRecordId,
        routeKey: cashbackRouteKey,
        stageNo: Number(cashbackStageNo || 3)
      })
      setCashbackPollRaw(formatJson(res.data))

      if (res.data?.authorized) {
        setCashbackPollingState('authorized')
        return
      }

      const qrText = res.data?.authorizeUrl || res.data?.qrContent || ''
      setCashbackQrCodeUrl(qrText)
      setCashbackBindToken(res.data?.bindToken || '')
      setCashbackPollingState(qrText && res.data?.bindToken ? 'pending' : 'idle')

      if (!qrText || !res.data?.bindToken) {
        setCashbackError('生成授权二维码失败，返回数据不完整')
      }
    } catch (error) {
      setCashbackError(error.response?.data?.error || error.message || '生成微信提现授权二维码失败')
    } finally {
      setCashbackLoading(false)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    )
  }

  if (!user) return null

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 dark:bg-gray-950 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Reminder 临时联调页</h1>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            当前登录用户：{user?.phone || user?.nickname || user?.id || '-'}。这个页面只用于测试 page4 的 9.9 元支付和 page3 的微信提现授权。
          </p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center">
                <Wallet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">page4 支付 9.9 元测试</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">固定走 `low_balance_unpaid + page4`，创建微信支付二维码。</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">stageNo</span>
                <select
                  value={page4StageNo}
                  onChange={(e) => setPage4StageNo(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">planId</span>
                <input
                  value={page4PlanId}
                  onChange={(e) => setPage4PlanId(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="默认 11"
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </label>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400">routeKey / pageKey</div>
              <div className="mt-2 text-sm font-medium text-gray-900 dark:text-white break-all">low_balance_unpaid / page4</div>
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                可直接点“创建 9.9 元支付单”，不必先读取活动；但后端仍会校验当前账号是否命中该路线的有效 stage。
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleLoadPage4Status}
                disabled={page4Loading}
                className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium"
              >
                {page4Loading ? '加载中...' : '读取 page4 活动'}
              </button>
              <button
                onClick={handleCreatePage4Order}
                disabled={page4Loading || !effectivePage4PlanId}
                className="px-5 py-3 rounded-xl border border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
              >
                创建 9.9 元支付单
              </button>
            </div>

            {page4Offer && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 space-y-2 text-sm">
                <div><span className="text-gray-500 dark:text-gray-400">planId：</span><span className="text-gray-900 dark:text-white">{page4Offer.planId}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">标题：</span><span className="text-gray-900 dark:text-white">{page4Offer.title}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">金额：</span><span className="text-gray-900 dark:text-white">¥{page4Offer.amount}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">总积分：</span><span className="text-gray-900 dark:text-white">{page4Offer.totalPoints}</span></div>
              </div>
            )}

            {page4Error && (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
                {page4Error}
              </div>
            )}

            {page4OrderNo && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 text-sm">
                <div className="text-gray-500 dark:text-gray-400">订单号</div>
                <div className="mt-1 font-medium text-gray-900 dark:text-white break-all">{page4OrderNo}</div>
              </div>
            )}

            {page4QrCodeUrl && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-6 flex flex-col items-center">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <QRCodeSVG value={page4QrCodeUrl} size={220} />
                </div>
                <p className="mt-4 text-gray-900 dark:text-white font-medium">请使用微信扫码支付 9.9 元</p>
                {page4OrderState === 'pending' && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    正在轮询订单状态
                  </p>
                )}
                {page4OrderState === 'success' && page4MrSync !== 0 && (
                  <p className="mt-2 text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    支付成功
                  </p>
                )}
                {page4OrderState === 'success' && page4MrSync === 0 && (
                  <p className="mt-2 text-sm text-amber-600 dark:text-amber-400 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4" />
                    {page4SyncTip}
                  </p>
                )}
              </div>
            )}

            <div className="rounded-xl bg-gray-950 text-gray-100 p-4 overflow-auto">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">page4 响应</div>
              <pre className="text-xs whitespace-pre-wrap break-all">{page4Raw || '暂无'}</pre>
            </div>
          </section>

          <section className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center">
                <QrCode className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">page3 微信授权测试</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">先读 `reward/status`，再生成微信提现授权二维码并轮询结果。</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">routeKey</span>
                <select
                  value={cashbackRouteKey}
                  onChange={(e) => setCashbackRouteKey(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  {CASHBACK_ROUTE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">stageNo</span>
                <select
                  value={cashbackStageNo}
                  onChange={(e) => setCashbackStageNo(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                >
                  <option value="3">3</option>
                </select>
              </label>
                <label className="block">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">rewardRecordId</span>
                  <input
                    value={cashbackRewardRecordId}
                    onChange={(e) => setCashbackRewardRecordId(e.target.value.replace(/[^\d]/g, ''))}
                    placeholder="可直接填写已有 rewardRecordId"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  />
                </label>
            </div>

            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400">直测说明</div>
              <div className="mt-2 text-sm text-gray-900 dark:text-white">
                直接填写 `rewardRecordId` 后即可生成二维码，不必先读取状态；后端仍会校验该奖励记录是否属于当前登录用户且类型为 `cashback_withdraw`。
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleLoadCashbackStatus}
                disabled={cashbackLoading}
                className="px-5 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-medium"
              >
                {cashbackLoading ? '加载中...' : '读取微信提现状态'}
              </button>
              <button
                onClick={handleCreateCashbackQrcode}
                disabled={cashbackLoading || !cashbackReadyForQrcode}
                className="px-5 py-3 rounded-xl border border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 dark:border-blue-800 dark:text-blue-300 dark:hover:bg-blue-900/20"
              >
                生成微信提现授权二维码
              </button>
            </div>

            {cashbackStatus && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 space-y-2 text-sm">
                <div><span className="text-gray-500 dark:text-gray-400">rewardRecordId：</span><span className="text-gray-900 dark:text-white">{cashbackStatus.rewardRecordId || '-'}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">rewardType：</span><span className="text-gray-900 dark:text-white">{cashbackStatus.rewardType || '-'}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">claimableAmount：</span><span className="text-gray-900 dark:text-white">{cashbackStatus.claimableAmount ?? '-'}</span></div>
                <div><span className="text-gray-500 dark:text-gray-400">wechatAuthorized：</span><span className="text-gray-900 dark:text-white">{String(Boolean(cashbackStatus.wechatAuthorized))}</span></div>
              </div>
            )}

            {cashbackError && (
              <div className="rounded-xl border border-red-200 bg-red-50 text-red-600 px-4 py-3 text-sm dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
                {cashbackError}
              </div>
            )}

            {cashbackBindToken && (
              <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-4 text-sm">
                <div className="text-gray-500 dark:text-gray-400">bindToken</div>
                <div className="mt-1 font-medium text-gray-900 dark:text-white break-all">{cashbackBindToken}</div>
              </div>
            )}

            {cashbackQrCodeUrl && (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 p-6 flex flex-col items-center">
                <div className="bg-white rounded-xl p-3 shadow-sm">
                  <QRCodeSVG value={cashbackQrCodeUrl} size={220} />
                </div>
                <p className="mt-4 text-gray-900 dark:text-white font-medium">请使用微信扫码授权提现</p>
                {cashbackPollingState === 'pending' && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    正在轮询授权结果
                  </p>
                )}
              </div>
            )}

            {cashbackPollingState === 'authorized' && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 px-4 py-3 text-sm flex items-center gap-2 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-400">
                <CheckCircle2 className="w-4 h-4" />
                微信授权已完成
              </div>
            )}

            {cashbackPollingState === 'expired' && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 text-amber-700 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-400">
                授权二维码已过期，请重新生成
              </div>
            )}

            <div className="rounded-xl bg-gray-950 text-gray-100 p-4 overflow-auto">
              <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">微信提现响应</div>
              <pre className="text-xs whitespace-pre-wrap break-all">{cashbackPollRaw || '暂无'}</pre>
            </div>
          </section>
        </div>
      </div>
    </div>
  )
}
